<template>
  <div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
      <div>
        <div class="border-b pb-6 pt-3">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div class="block">
                <span class="text-base font-semibold font-poppins text-gray-800">Automatic Turns</span>
                <button v-popover:auto-turn-info.right>
                  <i class="fas fa-info-circle text-gray-600 px-2"></i>
                </button>
              </div>
              <span class="text-gray-500">
                Automatically move the next tapped-in student to the speaker position.
              </span>
            </div>
            <toggle-button
              :labels="false"
              v-model="auto_turn"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
        </div>
        <popover
          class="bg-white drop-shadow absolute z-auto"
          name="auto-turn-info"
          :width="380"
        >
          <div class="px-6 py-4">
            Parlay will automatically add the next tapped in student into the
            speaker position after the current speaker taps out, or if the
            current speaker position is empty. Teachers can still manage the
            order of speakers using the queue.
          </div>
        </popover>
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins">Audio / Video</span>
                <button class="px-2" v-popover:audio-video-info.right>
                  <i class="fas fa-info-circle text-gray-600"></i>
                </button>
              </div>
              <p class="text-gray-500">
                Embedded audio/video functionality for remote RoundTables. Not required for transcription.
              </p>
            </div>
            <toggle-button
              :value="audio_video"
              :labels="false"
              :sync="true"
              @change="onChangeAudioVideo($event)"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class="bg-white drop-shadow absolute z-auto"
            name="audio-video-info"
            :width="400"
          >
            <div class="px-6 py-4">
              All users should have a working camera and a microphone for this
              functionality, but can turn off video and mute audio as desired.
            </div>
          </popover>
        </div>
        <div class="py-7 border-b">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div class="flex items-center space-x-1 py-1">
                <div class="flex items-center space-x-2">
                  <span class="text-base font-semibold font-poppins">Transcription</span>
                  <div class="px-2.5 flex items-center space-x-1 font-poppins py-0.5 bg-pink-50 text-xs border border-solid border-pinky rounded-full text-pinky font-semibold">
                    <span>BETA</span>
                  </div>
                </div>
              </div>
              <p class="text-gray-500">
                Create a turn by turn transcript from the discussion that can be analyzed by genie.
              </p>
            </div>
            <toggle-button
              :value="is_transcription_active"
              :labels="false"
              :sync="true"
              @change="onChangeTranscription($event)"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class="bg-white drop-shadow absolute z-auto"
            name="audio-video-info"
            :width="400"
          >
            <div class="px-6 py-4">
              All users should have a working camera and a microphone for this
              functionality, but can turn off video and mute audio as desired.
            </div>
          </popover>
        </div>
        <div class="py-7">
          <div class="flex justify-between items-center">
            <div class="pl-2.5">
              <div>
                <span class="text-base font-semibold font-poppins">Assessment</span>
                <button class="px-2" v-popover:assessment-info.right>
                  <i class="fas fa-info-circle text-gray-600"></i>
                </button>
              </div>
              <p class="text-gray-500">
                Teacher can assess students based on predefined criteria.
              </p>
            </div>
            <toggle-button
              :disabled="is_saving"
              :sync="true"
              :labels="false"
              v-model="assessment_status"
              color="#72ccd7"
              class="toggle-button-class"
            />
          </div>
          <popover
            class=" bg-white drop-shadow absolute z-auto"
            name="assessment-info"
            :width="350"
          >
            <div class="px-6 py-4">
              Create your own criteria or use one of the defaults. Set a Max
              Score for a rubric-style assessment, or leave it off for a
              tally-based approach.
            </div>
          </popover>
          <div v-if="assessment_status" class="mt-4 pl-2.5">
            <div class="flex flex-col sm:flex-row">
              <div
                class="bg-gray-100 border border-solid border-gray-200 text-gray-500 rounded p-0.5 max-w-xxs"
              >
                <button
                  class="w-16 py-1 rounded font-poppins"
                  :class="max_score_enabled === false ? 'bg-white border border-solid border-gray-200' : ''"
                  @click="max_score_enabled = !max_score_enabled"
                >
                  Tally
                </button>
                <button
                  class="w-16 py-1 rounded font-poppins"
                  :class="max_score_enabled === true ? 'bg-white border border-solid border-gray-200' : ''"
                  @click="max_score_enabled = !max_score_enabled"
                >
                  Rubric
                </button>
              </div>
              <div v-if="max_score_enabled" class="flex items-center mt-1 sm:mt-0 sm:ml-5">
                <div>
                  <p class="text-sm font-semibold font-poppins">Max Score</p>
                  <p class="text-gray-500 text-xs font-poppins">For each criteria</p>
                </div>
                <div class="ml-5 flex items-center space-x-1">
                  <button @click="point_scale -= 1" :disabled="Number(point_scale) === 0" class="text-gray-500 h-7 w-7 rounded-full hover:bg-gray-100">
                    <i class="fa fa-chevron-left text-xs"></i>
                  </button>
                  <input
                    :disabled="is_saving"
                    @input="inputed"
                    v-model="point_scale"
                    class="w-10 h-8 rounded text-center custom-border"
                  />
                  <button @click="addPoint" class="text-gray-500 h-7 w-7 rounded-full hover:bg-gray-100">
                    <i class="fa fa-chevron-right text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
            <div  v-for="(point_type, index) in available_point_types" class="mt-3":key="index">
              <div class="flex justify-between border border-solid border-gray-200 py-2.5 px-4 rounded hover:shadow-sm">
                <div class="flex items-center space-x-3 w-9/12">
                  <Popper 
                    :append-to-body="true"
                    :prevent-default="true"
                    trigger="clickToToggle"
                    :options="{
                      placement: 'right',
                      modifiers: { offset: { offset: '0,8px' } }
                    }"
                    @show="openPopper"
                    @hide="closePopper"
                  >
                    <div class="popper bg-white rounded-md shadow-sm relative w-64 pb-4" v-click-outside="closePopper">
                      <div class="py-2.5 font-semibold text-sm font-poppins text-left w-full px-3">
                        {{ point_type.title }}
                      </div>
                      <div v-if="point_type.description" class="text-left text-xs flex flex-col px-3 text-gray-700 font-poppins font-light">
                        <div class="html-content font-poppins font-light" v-html="typeof point_type.description === 'object' ? point_type.description.join('. ') : point_type.description"></div>
                      </div>
                      <div v-if="point_type.points_description && point_type.points_description.length > 0" class="mt-4 px-3">
                        <div v-for="(point) in point_type.points_description" class="flex flex-col text-left text-xs space-y-2">
                          <div v-if="point.description !== ''" class="flex items-center space-x-1 mb-2">
                            <div class=""><span class="font-semibold font-poppins text-gray-800">({{ point.value }}) Level: </span><span class="text-gray-700">{{ point.description }}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button type="button" class="h-full items-center font-semibold hover:underline font-poppins text-gray-600 hover:text-gray-700" slot="reference">
                      <i class="fa-solid fa-circle-info"></i>
                    </button>
                  </Popper>
                  <button class="text-sm hover:underline" @click="editAssessmentPoint(point_type)">
                    {{ point_type.title }}
                  </button>
                </div>
                <div class="flex">
                  <input
                    v-model="selected_points"
                    :value="point_type.tag"
                    type="checkbox"
                    class="cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <div class="mt-3">
              <div class="w-full flex items-center space-x-2">
                <input
                  class="px-4 py-2 w-full rounded-t placeholder-gray-500 bg-white border border-solid border-gray-200"
                  type="text"
                  v-model="point_title"
                  placeholder="Enter a new criteria title..."
                  @keyup.enter="saveNewPoint"
                />
                <button
                  @click="saveNewPoint()"
                  class="bg-blueGray-700 sample rounded text-white px-3 py-1.5 border border-solid border-blueGray-700 disabled:opacity-50"
                  :disabled="point_title === '' ? true : false"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="flex justify-between items-start pt-3">
          <div>
            <div>
              <span class="text-base font-semibold font-poppins">Sentence Stems</span>
              <button class="px-2" v-popover:sentence-info.left>
                <i class="fas fa-info-circle text-gray-600"></i>
              </button>
            </div>
            <p class="text-gray-500">
              Students can click use these sentence stems to help frame their
              contributions to the discussion.
            </p>
          </div>
          <div>
            <pl-menu :menuSize="1" direction="right">
              <button slot="menu_trigger" class="rounded">
                <div class="flex items-center space-x-2">
                  <country-flag
                    :country="language"
                    size="normal"
                    rounded
                    class="rounded-full"
                    style="margin-top: -10px;"
                  />
                  <button class="hover:bg-gray-100 w-6 h-6 rounded-full text-xs border border-solid border-gray-200">
                    <i class="fa fa-chevron-down items-center text-gray-700 py-auto"></i>
                  </button>
                </div>
              </button>
              <ul menu-content>
                <li v-for="country in countries_list" @click="changeLanguage(country.lang)" class="menu-option" :key="country.lang">
                  <span>
                    <country-flag
                      :country="country.lang"
                      size="normal"
                      rounded
                      style="margin-left: -15px;"
                    />
                  </span>
                </li>
              </ul>
            </pl-menu>
          </div>
        </div>
        
        <popover
          class="mr-10 p-0 bg-white drop-shadow absolute z-auto"
          name="sentence-info"
          :width="600"
        >
          <div class="px-6 py-4 font-semibold border-b">Information</div>
          <div class="px-6 py-4">
            Clicking on a sentence stem will auto-populate the text into the
            student notepad, where they can fill it out and reference it during
            their turn.
          </div>
        </popover>
        <div class="mt-4">
          <p class="font-semibold text-primary">
            <i class="fas fa-play mr-1"></i>
            {{ $t('new_idea') }}
          </p>
          <div
            v-for="(newidea, index) in new_idea_sentences"
            :key="index"
            class="mt-4 flex justify-between"
          >
            <span class="w-10/12 text-sm">
              {{ index + 1 }}.
              <span v-if="newidea.is_custom"> {{ newidea.sentence }} </span
              ><span v-else>{{ $t('stem_new_' + index) }}</span>
            </span>
            <div class="flex items-center">
              <i
                v-if="newidea.is_custom"
                :disabled="is_saving"
                class="fas fa-trash-alt mr-1.5 text-red-200 hover:text-red-700 cursor-pointer"
                @click="showDeleteNewIdeaModal(index)"
              ></i>
              <i
                v-if="newidea.is_custom"
                :disabled="is_saving"
                class="fas fa-edit mr-1.5 text-gray-200 hover:text-gray-500 cursor-pointer"
                @click="editNewIdeaSentence(index, newidea)"
              ></i>
              <input
                :disabled="is_saving"
                v-model="newidea.is_active"
                type="checkbox"
                class="cursor-pointer"
              />
            </div>
          </div>
          <div class="mt-4 flex items-center">
            <input
              class="px-4 py-2 w-full rounded placeholder-gray-500 bg-white border border-solid border-gray-200"
              type="text"
              v-model="new_idea_new"
              :placeholder="'Enter a new question...'"
              :disabled="is_saving"
              @keyup.enter="addNewIdeaSentence"
            />
            <div v-if="editing_new_idea_sentence" class="flex">
              <button
                class="h-8 w-12 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-sm ml-2 outline-none"
                @click="cancelEditNewIdeaSentence"
              >
                Cancel
              </button>
              <button
                class="h-8 w-12 bg-primary text-white rounded-sm ml-2 outline-none"
                @click="saveEditNewIdeaSentence"
              >
                Save
              </button>
            </div>
            <button v-else @click="addNewIdeaSentence" class="outline-none">
              <i class="fas fa-plus-circle text-2xl ml-3 text-gray-400" />
            </button>
          </div>
        </div>
        <div class="mt-6">
          <p class="font-semibold text-green-600">
            <i class="fas fa-square mr-1"></i>{{ $t('build_on') }}
          </p>
          <div
            v-for="(build_on, index) in build_on_sentences"
            :key="index"
            class="mt-4 flex justify-between"
          >
            <span class="w-10/12 text-sm"
              >{{ index + 1 }}.
              <span v-if="build_on.is_custom">{{ build_on.sentence }} </span>
              <span v-else>{{ $t('stem_build_' + index) }}</span>
            </span>
            <div class="flex items-center">
              <i
                v-if="build_on.is_custom"
                :disabled="is_saving"
                class="fas fa-trash-alt mr-1.5 text-red-200 hover:text-red-700 cursor-pointer"
                @click="showDeleteBuilOnModal(index)"
              ></i>
              <i
                v-if="build_on.is_custom"
                :disabled="is_saving"
                class="fas fa-edit mr-1.5 text-gray-200 hover:text-gray-500 cursor-pointer"
                @click="editBuilOnSentence(index, build_on)"
              ></i>
              <input
                :disabled="is_saving"
                v-model="build_on.is_active"
                type="checkbox"
                class="cursor-pointer"
              />
            </div>
          </div>
          <div class="mt-4 flex items-center">
            <input
              class="px-4 py-2 w-full rounded placeholder-gray-500 bg-white border border-solid border-gray-200"
              type="text"
              v-model="build_on_new"
              :placeholder="'Enter a new question...'"
              :disabled="is_saving"
              @keyup.enter="addBuildOnSentence"
            />
            <div v-if="editing_build_on_sentence" class="flex">
              <button
                class="h-8 w-12 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-sm ml-2 outline-none"
                @click="cancelEditBuildOnSentence"
              >
                Cancel
              </button>
              <button
                class="h-8 w-12 bg-primary text-white rounded-sm ml-2 outline-none"
                @click="saveEditBuildOnSentence"
              >
                Save
              </button>
            </div>
            <button v-else @click="addBuildOnSentence" class="outline-none">
              <i class="fas fa-plus-circle text-2xl ml-3 text-gray-400" />
            </button>
          </div>
        </div>
        <div class="mt-6">
          <p class="font-semibold text-pinky">
            <i class="fas fa-circle mr-1"></i>{{ $t('challenge') }}
          </p>
          <div
            v-for="(challenge, index) in challenge_sentences"
            :key="index"
            class="mt-4 flex justify-between"
          >
            <span class="w-10/12 text-sm"
              >{{ index + 1 }}.
              <span v-if="challenge.is_custom">{{ challenge.sentence }}</span>
              <span v-else>{{ $t('stem_challenge_' + index) }}</span>
            </span>
            <div class="flex items-center">
              <i
                v-if="challenge.is_custom"
                :disabled="is_saving"
                class="fas fa-trash-alt mr-1.5 text-red-200 hover:text-red-700 cursor-pointer"
                @click="showDeleteChallengeModal(index)"
              ></i>
              <i
                v-if="challenge.is_custom"
                :disabled="is_saving"
                class="fas fa-edit mr-1.5 text-gray-200 hover:text-gray-500 cursor-pointer"
                @click="editChallengeSentence(index, challenge)"
              ></i>
              <input
                :disabled="is_saving"
                v-model="challenge.is_active"
                type="checkbox"
                class="cursor-pointer"
              />
            </div>
          </div>
          <div class="mt-4 flex items-center">
            <input
              class="px-4 py-2 w-full rounded placeholder-gray-500 bg-white border border-solid border-gray-200"
              type="text"
              v-model="challenge_new"
              :placeholder="'Enter a new question...'"
              :disabled="is_saving"
              @keyup.enter="addChallengeSentence"
            />
            <div v-if="editing_challenge_sentence" class="flex">
              <button
                class="h-8 w-12 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-sm ml-2 outline-none"
                @click="cancelEditChallengeSentence"
              >
                Cancel
              </button>
              <button
                class="h-8 w-12 bg-primary text-white rounded-sm ml-2 outline-none"
                @click="saveEditChallengeSentence"
              >
                Save
              </button>
            </div>
            <button v-else @click="addChallengeSentence" class="outline-none">
              <i class="fas fa-plus-circle text-2xl ml-3 text-gray-400" />
            </button>
          </div>
        </div>
        <div class="mt-6">
          <p class="flex font-semibold text-darkBlue-dark">
            <img
              class="h-5 w-5 mr-1"
              src="/static/live_discussion/ld_question_icon.svg"
            />{{ $t('question') }}
          </p>
          <div
            v-for="(question, index) in question_sentences"
            :key="index"
            class="mt-4 flex justify-between"
          >
            <span class="w-10/12 text-sm"
              >{{ index + 1 }}.
              <span v-if="question.is_custom">{{ question.sentence }}</span>
              <span v-else>{{ $t('stem_question_' + index) }}</span>
            </span>
            <div class="flex items-center">
              <i
                v-if="question.is_custom"
                :disabled="is_saving"
                class="fas fa-trash-alt mr-1.5 text-red-200 hover:text-red-700 cursor-pointer"
                @click="showDeleteQuestionModal(index)"
              ></i>
              <i
                v-if="question.is_custom"
                :disabled="is_saving"
                class="fas fa-edit mr-1.5 text-gray-200 hover:text-gray-500 cursor-pointer"
                @click="editQuestionSentence(index, question)"
              ></i>
              <input
                :disabled="is_saving"
                v-model="question.is_active"
                type="checkbox"
                class="cursor-pointer"
              />
            </div>
          </div>
          <div class="mt-4 flex items-center">
            <input
              class="px-4 py-2 w-full rounded placeholder-gray-500 bg-white border border-solid border-gray-200"
              type="text"
              v-model="question_new"
              :placeholder="'Enter a new question...'"
              :disabled="is_saving"
              @keyup.enter="addQuestionSentence"
            />
            <div v-if="editing_question_sentence" class="flex">
              <button
                class="h-8 w-12 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-sm ml-2 outline-none"
                @click="cancelEditQuestionSentence"
              >
                Cancel
              </button>
              <button
                class="h-8 w-12 bg-primary text-white rounded-sm ml-2 outline-none"
                @click="saveEditQuestionSentence"
              >
                Save
              </button>
            </div>
            <button v-else @click="addQuestionSentence" class="outline-none">
              <i class="fas fa-plus-circle text-2xl ml-3 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <DialogDeleteConfirm
      v-if="show_delete_new_idea_modal"
      @close="closeDeleteModal"
      @actionConfirmed="removeNewIdeaSentence"
      optionalText="New Idea"
    />
    <DialogDeleteConfirm
      v-if="show_delete_build_on_modal"
      @close="closeDeleteModal"
      @actionConfirmed="removeBuildOnSentence"
      optionalText="Build On"
    />
    <DialogDeleteConfirm
      v-if="show_delete_challenge_modal"
      @close="closeDeleteModal"
      @actionConfirmed="removeChallengeSentence"
      optionalText="Challenge"
    />
    <DialogDeleteConfirm
      v-if="show_delete_questions_modal"
      @close="closeDeleteModal"
      @actionConfirmed="removeQuestionSentence"
      optionalText="Question"
    />
  </div>
</template>
<script>
  import { mapGetters } from 'vuex';
  import { filter, uniqBy, find } from 'lodash';
  import { EventBus } from 'utils/event-bus';
  import axios from 'axios';
  import DialogDeleteConfirm from 'components/dialogDeleteConfirm';
  //import i18n from 'i18n';
  import Popper from 'vue-popperjs';
  import CountryFlag from 'vue-country-flag';
  export default {
    data() {
      return {
        is_saving: false,
        auto_turn: true,
        audio_video: false,
        is_transcription_active: false,
        assessment_status: true,
        max_score_enabled: false,
        point_scale: 0,
        selected_points: [],
        point_title: null,
        point_description: null,
        point_title_error_message: '',
        point_description_error_message: '',
        new_idea_sentences: [],
        build_on_sentences: [],
        challenge_sentences: [],
        question_sentences: [],
        new_idea_new: '',
        build_on_new: '',
        challenge_new: '',
        question_new: '',
        editing_setence_stem: false,
        new_idea_to_edit: '',
        build_on_to_edit: '',
        challenge_to_edit: '',
        question_to_edit: '',
        editing_new_idea_index: -1,
        editing_build_on_index: -1,
        editing_challenge_index: -1,
        editing_question_index: -1,
        delete_index: -1,
        show_delete_new_idea_modal: false,
        show_delete_build_on_modal: false,
        show_delete_challenge_modal: false,
        show_delete_questions_modal: false,
        new_idea_sentence: '',
        new_build_on_sentence: '',
        new_challenge_sentence: '',
        new_question_sentence: '',
        editing_new_idea_sentence: false,
        editing_new_idea_sentence_index: null,
        editing_build_on_sentence: false,
        editing_build_on_sentence_index: null,
        editing_challenge_sentence: false,
        editing_challenge_sentence_index: null,
        editing_question_sentence: false,
        editing_question_sentence_index: null,
        language: 'gb',
        info_open: false
      };
    },
    components: {
      DialogDeleteConfirm,
      CountryFlag,
      Popper
    },
    watch: {
      handleChangeSettings: function () {
        this.$emit('handleSettingsChange', this.handleChangeSettings);
      },
    },
    computed: {
      ...mapGetters([
        'getProfile',
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getCourseDetail',
      ]),
      full_point_settings() {
        return this.selected_points.map((tag) =>
          find(this.available_point_types, (point) => point.tag === tag)
        );
      },
      available_point_types() {
        const coursePoints = filter(
          this.getCourseDetail.points_settings.peer_rewards,
          (reward) => {
            const { tag } = reward;
            const prefix = 'ld';
            return (
              tag.substring(0, 2) === prefix || tag.substring(0, 3) === 'all'
            );
          }
        );
        return uniqBy(
          [
            ...coursePoints,
            ...this.getCurrentDiscussion.points_settings.peer_rewards,
          ],
          'tag'
        ).filter((point) => point);
      },
      error_present() {
        return (
          this.point_description_error_message || this.point_title_error_message
        );
      },
      handleChangeSettings: function () {
        const properties = {
          auto_turn: this.auto_turn !== this.getCurrentDiscussion.auto_turn ? true : false,
          audio_video: this.audio_video !== this.getCurrentDiscussion.audio_discussion_is_enabled ? true : false,
          assessment_status: this.assessment_status !== this.getCurrentDiscussion.enable_grading ? true : false,
          is_transcription_active: this.is_transcription_active !== this.getCurrentDiscussion.is_transcription_enable ? true : false,
          max_score_enabled: this.is_max_score_enable !== this.max_score_enabled ? true : false,
          point_scale: this.max_score_value !== this.point_scale ? true : false,
          selected_points: this.arePointsExist(this.selected_points, this.selected_points_current) === true ? false : true,
          new_idea_sentences: this.arraysAreEqual(this.current_new_idea_sentences, this.new_idea_sentences_new) === true ? false : true,
          build_on_sentences: this.arraysAreEqual(this.current_buildon_sentences, this.buildon_sentences_new) === true ? false : true,
          question_sentences: this.arraysAreEqual(this.current_questions_sentences, this.questions_sentences_new) === true ? false : true,
          challenge_sentences: this.arraysAreEqual(this.current_challenge_sentences, this.challenge_sentences_new) === true ? false : true,
          language: this.language !== this.getCurrentDiscussion.language ? true : false,
        };
        const areAllFalse = Object.values(properties).every((value) => value === false);
        return areAllFalse;
      },
      is_max_score_enable: function () {
        let is_max_score_enable = false;
        this.getCurrentDiscussion.point_cap === null
          ? (is_max_score_enable = false)
          : (is_max_score_enable = true);
        return is_max_score_enable;
      },
      max_score_value: function () {
        let max_score_current_value;
        if (this.getCurrentDiscussion.point_cap !== null) {
          max_score_current_value = Number(this.getCurrentDiscussion.point_cap);
          return max_score_current_value;
        }
        return this.point_scale;
      },
      selected_points_current: function () {
        const currentPoints = [];
        this.getCurrentDiscussion.points_settings.peer_rewards.forEach(
          (point) => {
            currentPoints.push(point.tag);
          }
        );
        return currentPoints;
      },
      current_new_idea_sentences: function () {
        const currentSentences = [];
        this.getCurrentDiscussion.new_idea_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      new_idea_sentences_new: function () {
        const currentNewSentences = [];
        this.new_idea_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentNewSentences.push(sentence.sentence);
        });
        return currentNewSentences;
      },
      current_questions_sentences: function () {
        const currentSentences = [];
        this.getCurrentDiscussion.question_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      questions_sentences_new: function () {
        const currentNewSentences = [];
        this.question_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentNewSentences.push(sentence.sentence);
        });
        return currentNewSentences;
      },
      current_challenge_sentences: function () {
        const currentSentences = [];
        this.getCurrentDiscussion.challenge_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      challenge_sentences_new: function () {
        const currentSentences = [];
        this.challenge_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      current_buildon_sentences: function () {
        const currentSentences = [];
        this.getCurrentDiscussion.build_on_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      buildon_sentences_new: function () {
        const currentSentences = [];
        this.build_on_sentences.forEach((sentence) => {
          if (sentence.is_active === true)
            currentSentences.push(sentence.sentence);
        });
        return currentSentences;
      },
      countries_list() {
        return [
          { country: 'England', lang: 'gb' },
          { country: 'Spain', lang: 'es' },
          { country: 'France', lang: 'fr' },
          { country: 'Italy', lang: 'it' },
          { country: 'Germany', lang: 'de' },
          { country: 'China', lang: 'cn' },
          { country: 'Japan', lang: 'jp' },
        ];
      },
    },
    methods: {
      openPopper() {
        this.info_open = true;
      },
      closePopper() {
        this.info_open = false;
      },
      inputed() {
        if (this.point_scale === '.') {
          this.point_scale = '';
        }
        if (this.point_scale === '' || this.point_scale < 0) {
          this.point_scale = '0';
        }
        if (isNaN(this.point_scale)) {
          this.point_scale = this.point_scale.slice(0, -1);
        } else {
          this.point_scale = this.point_scale.trim();
        }
      },
      arraysAreEqual: function (a, b) {
        for (var i = 0; i < a.length; ++i) {
          if (a[i] !== b[i]) return false;
        }
        return true;
      },
      arePointsExist: function (array1, array2) {
        if (array1.length === array2.length) {
          return array1.every((element) => {
            if (array2.includes(element)) {
              return true;
            }
            return false;
          });
        }
        return false;
      },
      addPoint() {
        if (this.point_scale === '') {
          this.point_scale = 1;
          return;
        }
        let decimal_places = 0;
        this.point_scale = String(this.point_scale);
        if (this.point_scale.split('.')[1])
          decimal_places = this.point_scale.split('.')[1].length;
        this.point_scale = Number.parseFloat(this.point_scale);
        this.point_scale += 1;
        if (this.point_scale > this.pointCap) {
          this.point_scale = this.pointCap;
        }
        this.point_scale = String(this.point_scale.toFixed(decimal_places));
      },
      formatPointDescription(point) {
        const { description } = point;
        return Array.isArray(description)
          ? description.join('. ')
          : description;
      },
      createUniqueTag(title, activityType) {
        return `${activityType}${title
          .toLowerCase()
          .trim()
          .replace(/ /g, '_')}${'_'}${Math.random().toString(36).substr(2, 5)}`;
      },
      validateForm() {
        const toast = { type: "Error", message: "", time: 5000};
        const title_length = !this.point_title || (this.point_title && (this.point_title.trim().length < 2 || this.point_title.trim().length > 24));
        if (title_length) {
          toast.message = 'Point title needs to be between 1 and 25 characters';
          EventBus.$emit('createToast', toast);
          return;
        }
      },
      saveNewPoint: async function() {
        this.validateForm();

        const { point_title, point_description } = this;

        const prefix = 'all_';
        const new_point = {
          is_custom: true,
          description: point_description,
          tag: this.createUniqueTag(this.point_title, prefix),
          title: point_title,
          amount: 1,
          category: 'impression',
          icon: 'point.good_analysis.svg',
        };

        const clonedCoursePointSettings = JSON.parse(JSON.stringify(this.getCourseDetail.points_settings.peer_rewards));
        clonedCoursePointSettings.push(new_point);

        this.$store.dispatch('analytics','edit written roundtable - create own point type');
        await this.$store.dispatch('updateCourseDetail', {
          id: this.getCourseDetail.id,
          points_settings: { peer_rewards: clonedCoursePointSettings },
        });
        setTimeout(() => {
          this.$emit('editAssessmentPoint', 'live', new_point.tag, this.max_score_enabled ? this.point_scale : null);
        }, 3000);
        this.point_title = null;
        this.point_description = null;
          
      },
      editAssessmentPoint(point) {
        const tag = point.tag;
        console.log(this.max_score_enabled);
        console.log(this.point_scale);
        this.$emit('editAssessmentPoint', 'live', tag, this.max_score_enabled ? this.point_scale : null);
      },
      deleteAssessmentPoint(point) {
        const tag = point.tag;
        this.$emit('deleteAssessmentPoint', tag);
      },
      addNewIdeaSentence() {
        if (!this.new_idea_new) return;
        const { new_idea_sentences } = this.getCurrentDiscussion;
        const cloned_new_idea_sentences = JSON.parse(
          JSON.stringify(new_idea_sentences)
        );
        cloned_new_idea_sentences.push({
          sentence: this.new_idea_new,
          is_active: true,
          is_custom: true,
        });
        this.$store.dispatch(
          'analytics',
          'edit verbal discussion - added new idea sentence stem'
        );
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            new_idea_sentences: cloned_new_idea_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.new_idea_new = '';
            this.new_idea_sentences = cloned_new_idea_sentences;
          });
      },
      editNewIdeaSentence(index, sentence) {
        const { new_idea_sentences } = this.getCurrentDiscussion;
        this.new_idea_sentences = JSON.parse(
          JSON.stringify(new_idea_sentences)
        );

        this.editing_new_idea_sentence = true;
        this.editing_new_idea_sentence_index = index;
        this.new_idea_new = sentence.sentence;
      },
      saveEditNewIdeaSentence() {
        this.new_idea_sentences[this.editing_new_idea_sentence_index] = {
          sentence: this.new_idea_new,
          is_active:
            this.new_idea_sentences[this.editing_new_idea_sentence_index]
              .is_active,
          is_custom:
            this.new_idea_sentences[this.editing_new_idea_sentence_index]
              .is_custom,
        };
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            new_idea_sentences: this.new_idea_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.new_idea_new = '';
            this.editing_new_idea_sentence_index = null;
            this.editing_new_idea_sentence = false;
          });
      },
      cancelEditNewIdeaSentence() {
        this.new_idea_new = '';
        this.editing_new_idea_sentence_index = null;
        this.editing_new_idea_sentence = false;
      },
      showDeleteNewIdeaModal(index) {
        this.delete_index = index;
        this.show_delete_new_idea_modal = true;
      },
      removeNewIdeaSentence() {
        const { new_idea_sentences } = this.getCurrentDiscussion;
        const cloned_new_idea_sentences = JSON.parse(
          JSON.stringify(new_idea_sentences)
        );
        cloned_new_idea_sentences.splice(this.delete_index, 1);
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            new_idea_sentences: cloned_new_idea_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.delete_index = -1;
            this.new_idea_sentences = cloned_new_idea_sentences;
          })
          .catch(() => (this.delete_index = -1));
      },
      addBuildOnSentence() {
        if (!this.build_on_new) return;
        const { build_on_sentences } = this.getCurrentDiscussion;
        const cloned_build_on_sentences = JSON.parse(
          JSON.stringify(build_on_sentences)
        );
        cloned_build_on_sentences.push({
          sentence: this.build_on_new,
          is_active: true,
          is_custom: true,
        });
        this.$store.dispatch(
          'analytics',
          'edit verbal discussion - added new build on sentence stem'
        );
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            build_on_sentences: cloned_build_on_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.build_on_new = '';
            this.build_on_sentences = cloned_build_on_sentences;
          });
      },
      editBuilOnSentence(index, sentence) {
        const { build_on_sentences } = this.getCurrentDiscussion;
        this.build_on_sentences = JSON.parse(
          JSON.stringify(build_on_sentences)
        );
        this.editing_build_on_sentence = true;
        this.editing_build_on_sentence_index = index;
        this.build_on_new = sentence.sentence;
      },
      saveEditBuildOnSentence() {
        this.build_on_sentences[this.editing_build_on_sentence_index] = {
          sentence: this.build_on_new,
          is_active:
            this.build_on_sentences[this.editing_build_on_sentence_index]
              .is_active,
          is_custom:
            this.build_on_sentences[this.editing_build_on_sentence_index]
              .is_custom,
        };
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            build_on_sentences: this.build_on_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.build_on_new = '';
            this.editing_build_on_sentence_index = null;
            this.editing_build_on_sentence = false;
          });
      },
      cancelEditBuildOnSentence() {
        this.build_on_new = '';
        this.editing_build_on_sentence_index = null;
        this.editing_build_on_sentence = false;
      },
      showDeleteBuilOnModal(index) {
        this.delete_index = index;
        this.show_delete_build_on_modal = true;
      },
      removeBuildOnSentence() {
        const { build_on_sentences } = this.getCurrentDiscussion;
        const cloned_build_on_sentences = JSON.parse(
          JSON.stringify(build_on_sentences)
        );
        cloned_build_on_sentences.splice(this.delete_index, 1);
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            build_on_sentences: cloned_build_on_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.delete_index = -1;
            this.build_on_sentences = cloned_build_on_sentences;
          })
          .catch(() => (this.delete_index = -1));
      },
      addChallengeSentence() {
        if (!this.challenge_new) return;
        const { challenge_sentences } = this.getCurrentDiscussion;
        const cloned_challenge_sentences = JSON.parse(
          JSON.stringify(challenge_sentences)
        );
        cloned_challenge_sentences.push({
          sentence: this.challenge_new,
          is_active: true,
          is_custom: true,
        });
        this.$store.dispatch(
          'analytics',
          'edit verbal discussion - added new challenge sentence stem'
        );
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            challenge_sentences: cloned_challenge_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.challenge_new = '';
            this.challenge_sentences = cloned_challenge_sentences;
          });
      },
      editChallengeSentence(index, sentence) {
        const { challenge_sentences } = this.getCurrentDiscussion;
        this.challenge_sentences = JSON.parse(
          JSON.stringify(challenge_sentences)
        );

        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            challenge_sentences: this.challenge_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.editing_challenge_sentence = true;
            this.editing_challenge_sentence_index = index;
            this.challenge_new = sentence.sentence;
          });
      },
      saveEditChallengeSentence() {
        this.challenge_sentences[this.editing_challenge_sentence_index] = {
          sentence: this.challenge_new,
          is_active:
            this.challenge_sentences[this.editing_challenge_sentence_index]
              .is_active,
          is_custom:
            this.challenge_sentences[this.editing_challenge_sentence_index]
              .is_custom,
        };

        this.challenge_new = '';
        this.editing_challenge_sentence_index = null;
        this.editing_challenge_sentence = false;
      },
      cancelEditChallengeSentence() {
        this.challenge_new = '';
        this.editing_challenge_sentence_index = null;
        this.editing_challenge_sentence = false;
      },
      showDeleteChallengeModal(index) {
        this.delete_index = index;
        this.show_delete_challenge_modal = true;
      },
      removeChallengeSentence() {
        const { challenge_sentences } = this.getCurrentDiscussion;
        const cloned_challenge_sentences = JSON.parse(
          JSON.stringify(challenge_sentences)
        );
        cloned_challenge_sentences.splice(this.delete_index, 1);
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            challenge_sentences: cloned_challenge_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.delete_index = -1;
            this.challenge_sentences = cloned_challenge_sentences;
          })
          .catch(() => (this.delete_index = -1));
      },
      addQuestionSentence() {
        if (!this.question_new) return;
        const { question_sentences } = this.getCurrentDiscussion;
        const cloned_question_sentences = JSON.parse(
          JSON.stringify(question_sentences)
        );
        cloned_question_sentences.push({
          sentence: this.question_new,
          is_active: true,
          is_custom: true,
        });
        this.$store.dispatch(
          'analytics',
          'edit verbal discussion - added new question sentence stem'
        );
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            question_sentences: cloned_question_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.question_new = '';
            this.question_sentences = cloned_question_sentences;
          });
      },
      editQuestionSentence(index, sentence) {
        const { question_sentences } = this.getCurrentDiscussion;
        this.question_sentences = JSON.parse(
          JSON.stringify(question_sentences)
        );
        this.editing_question_sentence = true;
        this.editing_question_sentence_index = index;
        this.question_new = sentence.sentence;
      },
      saveEditQuestionSentence() {
        this.question_sentences[this.editing_question_sentence_index] = {
          sentence: this.question_new,
          is_active:
            this.question_sentences[this.editing_question_sentence_index]
              .is_active,
          is_custom:
            this.question_sentences[this.editing_question_sentence_index]
              .is_custom,
        };
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            question_sentences: this.question_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.question_new = '';
            this.editing_question_sentence_index = null;
            this.editing_question_sentence = false;
          });
      },
      cancelEditQuestionSentence() {
        this.question_new = '';
        this.editing_question_sentence_index = null;
        this.editing_question_sentence = false;
      },
      showDeleteQuestionModal(index) {
        this.delete_index = index;
        this.show_delete_questions_modal = true;
      },
      removeQuestionSentence() {
        const { question_sentences } = this.getCurrentDiscussion;
        const cloned_question_sentences = JSON.parse(
          JSON.stringify(question_sentences)
        );
        cloned_question_sentences.splice(this.delete_index, 1);
        this.$store
          .dispatch('saveLiveDiscussionSettings', {
            question_sentences: cloned_question_sentences,
            id: this.getCurrentDiscussion.id,
          })
          .then(() => {
            this.delete_index = -1;
            this.question_sentences = cloned_question_sentences;
          })
          .catch(() => (this.delete_index = -1));
      },
      closeDeleteModal() {
        this.delete_index = -1;
        this.show_delete_new_idea_modal = false;
        this.show_delete_build_on_modal = false;
        this.show_delete_challenge_modal = false;
        this.show_delete_questions_modal = false;
      },

      saveSettings: async function() {
        this.is_saving = true;
        const payload = {
          id: this.getCurrentDiscussion.id,
          auto_turn: this.auto_turn,
          audio_discussion_is_enabled: this.audio_video,
          enable_grading: this.assessment_status,
          point_cap: this.max_score_enabled ? this.point_scale : null,
          points_settings: this.full_point_settings,
          new_idea_sentences: this.new_idea_sentences,
          build_on_sentences: this.build_on_sentences,
          challenge_sentences: this.challenge_sentences,
          question_sentences: this.question_sentences,
          language: this.language,
          is_transcription_enable: this.is_transcription_active,
        };

        if (!this.audio_video) {
          EventBus.$emit('end-live-audio');
          await axios.put(`/api/live_discussions/${this.getCurrentDiscussion.id}/stop-share-screen`);
        }
        if (this.getCurrentDiscussion.audio_discussion_is_enabled === true) {
          if (this.audio_video !== this.getCurrentDiscussion.audio_discussion_is_enabled) {
            this.$store.dispatch('endLiveDiscussion', this.getCurrentDiscussion.id);
          }
        }
        this.$store.dispatch('saveLiveDiscussionSettings', payload).then(() => {
            this.is_saving = false;
            const toast = { type: 'Success', message: 'RoundTable settings successfully saved', time: 5000 };
            EventBus.$emit('createToast', toast);
          })
          .catch(() => {
            this.is_saving = false;
            const toast = {
              type: 'Error',
              message: 'Something went wrong, please try again.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
        this.$store.dispatch('updateStudentPointSettings', {
          points_settings: this.full_point_settings,
        });
      },
      changeLanguage(lang) {
        this.$i18n.locale = lang;
        this.language = lang;
      },
      onChangeAudioVideo(event) {
        if (!this.getProfile.is_paid) {
          const text ='ability to access direct audio/video integration for your Verbal RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.audio_video = event.value;

        if (this.is_transcription_active === true && event.value === false) {
          this.is_transcription_active = false;
        }
      },
      onChangeTranscription(event) {
        this.is_transcription_active = event.value;
      },
    },
    created() {
      this.auto_turn = this.getCurrentDiscussion.auto_turn;
      this.audio_video = this.getCurrentDiscussion.audio_discussion_is_enabled;
      this.is_transcription_active = this.getCurrentDiscussion.is_transcription_enable;
      this.assessment_status = this.getCurrentDiscussion.enable_grading;
      this.max_score_enabled = this.getCurrentDiscussion.point_cap ? true : false;
      this.point_scale = this.getCurrentDiscussion.point_cap ? this.getCurrentDiscussion.point_cap : 4;
      this.selected_points = this.getCurrentDiscussion.points_settings.peer_rewards.filter((point) => point).map((point) => point.tag);
      this.is_transcription_active = this.getCurrentDiscussion.is_transcription_enable;
      const { new_idea_sentences } = this.getCurrentDiscussion;
      this.new_idea_sentences = JSON.parse(JSON.stringify(new_idea_sentences));
      const { build_on_sentences } = this.getCurrentDiscussion;
      this.build_on_sentences = JSON.parse(JSON.stringify(build_on_sentences));
      const { challenge_sentences } = this.getCurrentDiscussion;
      this.challenge_sentences = JSON.parse(JSON.stringify(challenge_sentences));
      const { question_sentences } = this.getCurrentDiscussion;
      this.question_sentences = JSON.parse(JSON.stringify(question_sentences));
    },
    mounted() {
      this.$i18n.locale = this.getCurrentDiscussion.language;
      this.language = this.getCurrentDiscussion.language;
      EventBus.$on('save-settings', async () => {
        this.saveSettings();
      });
    },
    beforeDestroy() {
      EventBus.$off('save-settings');
    },
  };
</script>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.25rem !important;
    height: 1.25rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1.25rem;
    border-radius: 0.25rem;
    --tw-bg-opacity: 1;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1.25rem;
  }
</style>



// WEBPACK FOOTER //
// LDSettingsTab.vue?3e813357